import React, { useRef } from 'react';
import { FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import LiteralsService from 'src/services/literals/LiteralsService';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import styles from './BudgetPersonalizeView.module.scss';
import budgetStyles from '../budgetStyles.module.scss';
import Card from 'src/components/atomic/atoms/card/Card';
import { ButtonPrimary, ButtonSecondary, IconPlusCircle } from '@americanexpress/dls-react';
import { useHistory, useLocation } from 'react-router-dom';
import BudgetCategoryRow from 'src/components/atomic/organisms/budgetCategoryRow/BudgetCategoryRow';
import { BudgetModel } from 'src/types/budget.model';
import { useBudgets, useDefaultBudgets, useUpdateBudgets } from 'src/hooks/queries/budgets';
import { useCategories } from 'src/hooks/queries/categories';
import AmountCallout from 'src/components/budgets/amount-callout/AmountCallout';
import BudgetService from 'src/services/budget.service';
import { BudgetPersonalizeViewModel } from 'src/types/view-models/budgets/budget-personalize-view.model';
import _ from 'lodash';
import { useUser } from 'src/hooks/queries/users';
import { UserUtils } from 'src/types/user.model';
import { useSpendSmartDocumentTitle } from 'src/hooks/useDocumentTitle';
import { goToCurrentBudget } from 'src/services/router.service';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import colors from 'src/styles/colors.module.scss';
import { trackClick, TrackedErrors, trackError, useTrackPage } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import CategoryOverSpendingWarningBanner from 'src/components/atomic/molecules/categoryOverSpendingWarningBanner/CategoryOverSpendingWarningBanner';
import PersonalizeCategoryHeader from 'src/components/atomic/molecules/personalizeCategoryHeader/PersonalizeCategoryHeader';
import { TransactionType } from 'src/types/transaction-type.enum';

const removeBudget = (arrayHelpersRemove: (index: number) => void, index: number, budgets: BudgetModel[]): void => {
    const indexesToRemove: number[] = [index];
    // if removing a parent category, we also need to remove all of its subcategories
    if (budgets[index].groupId && !budgets[index].parentGroupId) {
        budgets.forEach((budget, currentIndex) => {
            if (budget.parentGroupId === budgets[index].groupId) {
                // create the array in reverse numerical order so the indexes stay accurate
                indexesToRemove.unshift(currentIndex);
            }
        });
    }
    indexesToRemove.forEach((removeIndex) => {
        arrayHelpersRemove(removeIndex);
    });
};

const removeSubcategories = (
    arrayHelpersRemove: (index: number) => void,
    budgets: BudgetModel[],
    oldBudget: BudgetModel
): void => {
    if (oldBudget.groupId && !oldBudget.parentGroupId) {
        const indexesToRemove: number[] = [];
        budgets.forEach((budget, currentIndex) => {
            if (budget.parentGroupId === oldBudget.groupId) {
                // create the array in reverse numerical order so the indexes stay accurate
                indexesToRemove.unshift(currentIndex);
            }
        });
        indexesToRemove.forEach((removeIndex) => {
            arrayHelpersRemove(removeIndex);
        });
    }
};

const addSubcategory = (
    arrayHelpersInsert: (index: number, budget: Partial<BudgetModel>) => void,
    index: number,
    parentBudget: BudgetModel,
    setFieldTouched: (field: string) => void
): void => {
    // require a parent category to be selected for a subcategory to be added
    if (!parentBudget.groupId) {
        setFieldTouched(`budgets[${index}].groupId`);
    } else {
        arrayHelpersInsert(index + 1, {
            groupId: undefined,
            monthlyAmount: undefined,
            parentGroupId: parentBudget.groupId,
            uniqueId: Math.floor(Math.random() * 1000).toString()
        });
    }
};

const updateSubcategories = (
    arrayHelpersRemove: (index: number) => void,
    budgets: BudgetModel[],
    oldBudget: BudgetModel,
    newGroupId: string,
    setFieldValue: (field: string, value: any) => void
): void => {
    if (oldBudget.groupId && !oldBudget.parentGroupId) {
        const indexesToRemove: number[] = [];
        budgets.forEach((budget, index) => {
            if (budget.parentGroupId === oldBudget.groupId) {
                if (!newGroupId) {
                    // create the array in reverse numerical order so the indexes stay accurate
                    indexesToRemove.unshift(index);
                } else {
                    setFieldValue(`budgets[${index}].parentGroupId`, newGroupId);
                    setFieldValue(`budgets[${index}].groupId`, undefined);
                }
            }
        });
        indexesToRemove.forEach((removeIndex) => {
            arrayHelpersRemove(removeIndex);
        });
    }
};

export default function BudgetPersonalizeView({ history }: BudgetPersonalizeViewModel): JSX.Element {
    useSpendSmartDocumentTitle(LiteralsService.getLiteral('Budget.titles.personalize'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.personalize'));
    const { isMinWidthLarge } = useBreakpoints();
    const routerHistory = useHistory();
    const { pathname } = useLocation();
    const { isLoading: isLoadingUser, data: user } = useUser();
    const { isLoading: isLoadingCurrentBudgets, data: currentBudgets } = useBudgets();
    const { isLoading: isLoadingDefaultBudgets, data: defaultBudgets } = useDefaultBudgets();
    const { isLoading: isLoadingCategories, data: categories } = useCategories(TransactionType.TRANSACTION_EXPENSE);
    const { mutateAsync: updateBudgets } = useUpdateBudgets();
    const isLoading = isLoadingUser || isLoadingCurrentBudgets || isLoadingDefaultBudgets || isLoadingCategories;

    const isFullPlanUser: boolean = !UserUtils.isBudgetingUser(user);

    const trackedErrors = useRef<TrackedErrors[]>([{ error: '', id: '' }]);

    const checkForDuplicateErrors = (currentBudget: Partial<BudgetModel>): void => {
        const uniqueId: string = `${currentBudget.parentGroupId}-${currentBudget.monthlyAmount.toString()}-${
            currentBudget.uniqueId
        }`;
        // return true if the error matches any error in the trackedErrors array
        if (
            trackedErrors.current.some((trackedErrors) => trackedErrors.error === adobeTags.Error.Subcategory) &&
            trackedErrors.current.some((trackedErrors) => trackedErrors.id === uniqueId)
        )
            return;
        trackError(adobeTags.Error.Subcategory, true);
        trackedErrors.current.push({ error: adobeTags.Error.Subcategory, id: uniqueId });
    };
    let initialValueBudgets: BudgetModel[];
    if (BudgetService.getCategoryBudgets(currentBudgets)?.length > 0) {
        // sort subcategories to be after parent so that they display grouped correctly
        initialValueBudgets = _.flatten(BudgetService.budgetsGroupedByCategory(currentBudgets || [], categories || []));
    } else {
        // monthlyAmount defaults to 0 from server, but we want the inputs to be empty
        initialValueBudgets = defaultBudgets?.map((budget) => ({ ...budget, monthlyAmount: undefined }));
    }

    const handleSave = async (values: { budgets: BudgetModel[] }): Promise<void> => {
        const monthlyBudget = BudgetService.getMonthlyBudget(currentBudgets);
        const newBudgets = monthlyBudget ? [...values.budgets, monthlyBudget] : values.budgets;
        const hasCategoryBudgets = values.budgets.length > 0;
        await updateBudgets(newBudgets, {
            onSuccess: (): void => {
                goToCurrentBudget(
                    isFullPlanUser,
                    history || routerHistory,
                    hasCategoryBudgets ? '?savedBudgets=true' : ''
                );
            }
        });
    };

    // Scrolls to and focuses on the first error element on the form, if it is an input field it also selects the value
    const focusOnError = (errors: any): void => {
        const errorObject = errors.budgets;
        if (errorObject) {
            const rowIndex: number = errorObject.findIndex((element: any) => element !== undefined);
            if (rowIndex >= 0) {
                const errorElement = errorObject[rowIndex]?.groupId
                    ? document.getElementById(`budgets[${rowIndex}].groupId`)
                    : (document.getElementById(`budgets[${rowIndex}].monthlyAmount`) as HTMLInputElement);
                errorElement.scrollIntoView({ behavior: 'smooth' });
                errorElement.focus();
                if (errorElement instanceof HTMLInputElement) {
                    errorElement.select();
                }
            }
        }
    };

    const titleElement = (
        <Typography
            component="h1"
            variant={isMinWidthLarge ? TypographyVariants.HEADING_XL : TypographyVariants.HEADING_M}
            className={colors.textLight}
        >
            {LiteralsService.getLiteral('Budget.Create.title')}
        </Typography>
    );

    const validationSchema = Yup.object({
        budgets: Yup.array().of(
            Yup.object()
                .shape({
                    groupId: Yup.string().required(LiteralsService.getLiteral('FormErrors.categoryRequired')),
                    monthlyAmount: Yup.number()
                        .required(LiteralsService.getLiteral('FormErrors.amountRequired'))
                        .moreThan(0, LiteralsService.getLiteral('FormErrors.amountGreaterThanZero'))
                })
                .test('subcategories are less than category total', '', (currentBudget, context) => {
                    const { parentGroupId } = currentBudget;
                    if (!parentGroupId) return true;
                    const budgets = context.parent;
                    if (
                        BudgetService.getParentCategoryTotal(budgets, parentGroupId) >
                        BudgetService.getParentBudgetMonthlyAmount(budgets, parentGroupId)
                    ) {
                        setTimeout(() => checkForDuplicateErrors(currentBudget), 500);
                        return context.createError({
                            path: `${context.path}.monthlyAmount`,
                            message: LiteralsService.getLiteral('Budget.Create.subcategoryTotalError')
                        });
                    }
                    return true;
                })
        )
    });

    return (
        <WideView
            titleElement={titleElement}
            subtitle={LiteralsService.getLiteral('Budget.Create.subtitle')}
            hasBackgroundBanner
            showFooter
            hideBackButton
            withDocTitle={false}
            data-cy={Selectors.SpendSmart.PERSONALIZE_ROOT}
        >
            <Card showBackgroundBanner>
                {isLoading ? (
                    <Loading className={styles.loading} />
                ) : (
                    <Formik
                        initialValues={{ budgets: initialValueBudgets }}
                        validationSchema={validationSchema}
                        validateOnBlur={false}
                        onSubmit={handleSave}
                    >
                        {({ values, setFieldTouched, setFieldValue, errors, isSubmitting }) => (
                            <Form className={styles.formWrapper}>
                                <FieldArray
                                    validateOnChange={false}
                                    name="budgets"
                                    render={(arrayHelpers) => (
                                        <>
                                            <CategoryOverSpendingWarningBanner />
                                            <div className={styles.categorySelection}>
                                                <PersonalizeCategoryHeader />
                                                {isSubmitting && focusOnError(errors)}
                                                {BudgetService.getCategoryBudgets(values.budgets)?.length > 0 ? (
                                                    BudgetService.getCategoryBudgets(values.budgets).map(
                                                        (budget, index) => {
                                                            return (
                                                                <React.Fragment key={`${budget.groupId}-${index}`}>
                                                                    <BudgetCategoryRow
                                                                        index={index}
                                                                        hasAvailableSubcategories={BudgetService.hasAvailableCategoryOptions(
                                                                            values.budgets,
                                                                            categories,
                                                                            true,
                                                                            index
                                                                        )}
                                                                        removeRow={() =>
                                                                            removeBudget(
                                                                                arrayHelpers.remove,
                                                                                index,
                                                                                values.budgets
                                                                            )
                                                                        }
                                                                        addSubcategory={() =>
                                                                            addSubcategory(
                                                                                arrayHelpers.insert,
                                                                                index,
                                                                                budget,
                                                                                setFieldTouched
                                                                            )
                                                                        }
                                                                        removeSubcategories={() =>
                                                                            removeSubcategories(
                                                                                arrayHelpers.remove,
                                                                                values.budgets,
                                                                                budget
                                                                            )
                                                                        }
                                                                        updateSubcategories={(groupId: string) =>
                                                                            updateSubcategories(
                                                                                arrayHelpers.remove,
                                                                                values.budgets,
                                                                                budget,
                                                                                groupId,
                                                                                setFieldValue
                                                                            )
                                                                        }
                                                                    />
                                                                    {budget.parentGroupId &&
                                                                        budget.parentGroupId !==
                                                                            values.budgets[index + 1]
                                                                                ?.parentGroupId && (
                                                                            <AmountCallout
                                                                                leftText={LiteralsService.getLiteral(
                                                                                    'Budget.Create.amountAvailable'
                                                                                )}
                                                                                amount={BudgetService.getCategoryAmountAvailable(
                                                                                    values.budgets,
                                                                                    budget.parentGroupId
                                                                                )}
                                                                                disabled={
                                                                                    BudgetService.getCategoryAmountAvailable(
                                                                                        values.budgets,
                                                                                        budget.parentGroupId
                                                                                    ) === '$--'
                                                                                }
                                                                            />
                                                                        )}
                                                                </React.Fragment>
                                                            );
                                                        }
                                                    )
                                                ) : (
                                                    <div className={styles.categoryEmptyState}>
                                                        <Typography
                                                            variant={TypographyVariants.BODY_BOLD}
                                                            component="p"
                                                        >
                                                            {LiteralsService.getLiteral('Budget.Create.noCategories')}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.addCategoryRow}>
                                                <ButtonSecondary
                                                    icon={<IconPlusCircle />}
                                                    disabled={
                                                        !BudgetService.hasAvailableCategoryOptions(
                                                            values.budgets,
                                                            categories
                                                        )
                                                    }
                                                    onClick={() => {
                                                        arrayHelpers.insert(values.budgets?.length, {
                                                            groupId: '',
                                                            monthlyAmount: undefined
                                                        });
                                                        trackClick(pathname, adobeTags.Personalize.Category.Add);
                                                    }}
                                                    className={styles.fullWidthOnMobile}
                                                    data-cy={Selectors.SpendSmart.ADD_CATEGORY_BUTTON}
                                                >
                                                    {LiteralsService.getLiteral('Budget.Create.addCategory')}
                                                </ButtonSecondary>
                                            </div>
                                        </>
                                    )}
                                />
                                <div className={budgetStyles.budgetBottomButtons}>
                                    <ButtonSecondary
                                        onClick={() => {
                                            trackClick(pathname, adobeTags.Personalize.Cancel);
                                            goToCurrentBudget(isFullPlanUser, history || routerHistory);
                                        }}
                                        className={styles.fullWidthOnMobile}
                                        data-cy={Selectors.SpendSmart.PERSONALIZE_CANCEL_BUTTON}
                                    >
                                        {LiteralsService.getLiteral('CommonText.cancel')}
                                    </ButtonSecondary>
                                    <ButtonPrimary
                                        type="submit"
                                        onClick={() => trackClick(pathname, adobeTags.Personalize.Confirm)}
                                        className={styles.fullWidthOnMobile}
                                        data-cy={Selectors.SpendSmart.PERSONALIZE_CONFIRM_BUTTON}
                                    >
                                        {LiteralsService.getLiteral('CommonText.confirm')}
                                    </ButtonPrimary>
                                </div>
                            </Form>
                        )}
                    </Formik>
                )}
            </Card>
        </WideView>
    );
}
